import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/api/errors'
import type { CodePreview, DeskListResponse } from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'
import { deskBatches } from '@/lib/desk'
import { Card, CodeInput, Icon } from '@/ui'
import { deskListQuery, previewCode } from './api'
import styles from './desk.module.css'
import type { HandoverSubject } from './HandoverModal'
import { deskStrings } from './strings'

function resolve(entries: DeskListResponse['entries'], p: CodePreview): HandoverSubject | null {
  if (p.kind === 'guest') {
    const g = entries.find((e) => e.kind === 'guest' && p.item_ids.includes(e.id))
    return g?.kind === 'guest' ? g : null
  }
  return deskBatches(entries).find((b) => b.items.some((i) => p.item_ids.includes(i.id))) ?? null
}

/** Code-first lookup: six characters resolve to a unit's waiting batch or to a guest. */
export function CodeLookup({
  condoId,
  onMatch,
}: {
  condoId: number
  onMatch: (subject: HandoverSubject, code: string) => void
}) {
  const qc = useQueryClient()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const t = deskStrings.codeLookup

  async function lookup(value: string) {
    try {
      const preview = await previewCode(condoId, value)
      const cached = qc.getQueryData<DeskListResponse>(queryKeys.desk.list(condoId, 'waiting'))
      let subject = cached ? resolve(cached.entries, preview) : null
      if (!subject) {
        // The list is older than the code: refetch before giving up.
        const fresh = await qc.fetchQuery({ ...deskListQuery(condoId, 'waiting'), staleTime: 0 })
        subject = resolve(fresh.entries, preview)
      }
      if (!subject) {
        setError(t.invalid)
        return
      }
      setCode('')
      onMatch(subject, value)
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) setError(t.invalid)
      else if (e instanceof ApiError && e.status === 409) {
        setError(t.used)
        void qc.invalidateQueries({ queryKey: queryKeys.desk.all(condoId) })
      } else setError(t.failed)
    }
  }

  return (
    <Card pad={20} className={styles.lookup}>
      <div className={styles.lookupBadge}>
        <Icon name="key" size={20} />
      </div>
      <div className={styles.lookupText}>
        <div className={styles.lookupTitle}>{t.title}</div>
        <div className={styles.lookupDescription}>{t.description}</div>
      </div>
      <div className={styles.lookupInput}>
        <CodeInput
          label={t.label}
          value={code}
          error={error || undefined}
          onChange={(v) => {
            setCode(v)
            setError('')
            if (v.length === 6) void lookup(v)
          }}
        />
      </div>
    </Card>
  )
}
